import type {ReactNode} from 'react';
import React from 'react';

import AnnotatableContent from './AnnotatableContent';
import AnnotationSupplementNoteIndicator from './AnnotationSupplementNoteIndicator';
import {buildSupplementRegionId} from './annotationRegions';
import {ANNOTATE_IGNORE_PROPS} from './annotationTextProjection';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';
import {useSupplementCard} from './useSupplementCard';

interface AnnotatablePassageSupplementProps {
  supplementId: string;
  /** Language of the rendered passage ('es' when translated); null = original. */
  lang?: string | null;
  children: ReactNode;
}

/**
 * A passage takes precise text marks (#18667: whole-passage notes weren't useful
 * enough) in its own region, so they carry across the questions sharing it and its
 * collapse toggle stops resizing the prompt's offsets under every later mark.
 */
export default function AnnotatablePassageSupplement({
  supplementId,
  lang = null,
  children
}: AnnotatablePassageSupplementProps) {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();

  if (!isAnnotationModeAvailable) {
    return <>{children}</>;
  }

  return (
    <PassageRegion supplementId={supplementId} lang={lang}>
      {children}
    </PassageRegion>
  );
}

function PassageRegion({
  supplementId,
  lang,
  children
}: {
  supplementId: string;
  lang: string | null;
  children: ReactNode;
}) {
  const regionId = buildSupplementRegionId(supplementId);
  const {regionRef, region, cardClassName, notedMarks} = useSupplementCard(regionId);

  return (
    <>
      <div
        {...ANNOTATE_IGNORE_PROPS}
        ref={regionRef}
        data-testid='annotatable-passage-supplement'
        className={cardClassName}
      >
        {/* `contents`: the fence stays the passage's only added box. */}
        <AnnotatableContent
          regionId={regionId}
          lang={lang}
          className='contents'
          showNoteIndicators={false}
        >
          {children}
        </AnnotatableContent>
      </div>
      <AnnotationSupplementNoteIndicator region={region} notedMarks={notedMarks} />
    </>
  );
}
